import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    message,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Table,
    Row,
    Col,
    Divider,
    Switch
} from "antd";
import {LeftOutlined} from "@ant-design/icons";
import api from "../../api";
import dayjs from "dayjs";
import {NoWrap, boldLabelStyle, maxW700} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;

const CCCustomerDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<any>(null);
    const [trips, setTrips] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCustomer();
        fetchTrips();
    }, [id]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await api.get("/users/");
                const usersArray = Array.isArray(res.data) ? res.data : res.data.results || [];
                setUsers(usersArray);
            } catch {
                message.error("Failed to load users");
            }
        };
        fetchUsers();
    }, []);

    const fetchCustomer = async () => {
        try {
            const res = await api.get(`/cc/customers/${id}/`);
            setCustomer(res.data);
        } catch (err) {
            message.error("Failed to load customer");
        } finally {
            setLoading(false);
        }
    };

    const fetchTrips = async () => {
        try {
            const res = await api.get("/cc/trips/", {
                params: {customer_id: id, page: 1, page_size: 1000},
            });
            setTrips(res.data.results);
        } catch (err) {
            message.error("Failed to load trips");
        }
    };

    const showEditModal = () => {
        if (!customer) return;
        form.setFieldsValue({
            ...customer,
            email: customer.registration?.email || "",
            person_in_charge_id: customer.person_in_charge?.id ?? undefined,
            dob: customer.dob ? dayjs(customer.dob) : null,
            passport_expiration: customer.passport_expiration ? dayjs(customer.passport_expiration) : null,
            slot_player: !!customer.slot_player,
            strong_slot_player: !!customer.strong_slot_player,
        });
        setIsModalVisible(true);
    };

    const handleUpdate = async (values: any) => {
        try {
            const {email, ...rest} = values;

            const payload = {
                ...rest,
                person_in_charge: rest.person_in_charge || null,
                dob: rest.dob?.format?.("YYYY-MM-DD"),
                passport_expiration: rest.passport_expiration?.format?.("YYYY-MM-DD"),
                slot_player: !!rest.slot_player,
                strong_slot_player: !!rest.strong_slot_player,
            };

            // Ensure we never send the read-only field
            delete payload.person_in_charge;

            // Coerce to number if present; omit if untouched
            if (values.person_in_charge_id !== undefined) {
                payload.person_in_charge_id = Number(values.person_in_charge_id);
            } else {
                delete payload.person_in_charge_id;
            }

            await api.put(`/cc/customers/${id}/`, payload);


            const currentEmail = customer?.registration?.email || "";
            const registrationId = customer?.registration?.id;

            if (registrationId && typeof email === "string" && email.trim() !== currentEmail) {
                try {
                    await api.patch(`/cc/registrations/${registrationId}/`, {email: email.trim()});
                } catch (regErr) {
                    console.error("Registration email update failed:", regErr);
                    // Optional: surface a softer warning while still considering the main update a success
                    message.warning("Customer saved, but updating the email failed.");
                }
            }

            message.success("Customer updated successfully.");
            setIsModalVisible(false);
            fetchCustomer();
        } catch (err: any) {
            console.error("PUT error:", err?.response?.data || err);
            message.error("Update failed.");
        }
    };

    const tripColumns = [
        {title: "Check-in", dataIndex: ["hotel", "checkin_date"]},
        {title: "Check-out", dataIndex: ["hotel", "checkout_date"]},
        {title: "Buy-in Method", dataIndex: ["trip", "buy_in_method"]},
        {
            title: "Buy-in Amount",
            dataIndex: ["trip", "buyin_amount"],
            render: (_: any, rec: any) => {
                const amount = rec.trip?.buyin_amount;
                const currency = rec.trip?.buyin_currency;
                if (amount == null) return "-";
                const symbolMap: Record<string, string> = {KRW: "₩", Yen: "¥", HKD: "HK$", NTD: "NT$", USDT: "US$"};
                const symbol = symbolMap[currency || ""] || "";
                return `${symbol}${Number(amount).toLocaleString()}`;
            },
        },
        {
            title: "Total Flight Cost (KRW)",
            dataIndex: ["flight", "total_flight_cost_krw"],
            render: (v: number | null | undefined) => `₩${Number(v ?? 0).toLocaleString()}`,
        },
        {
            title: "Additional Expenses",
            dataIndex: ["trip", "additional_expenses"],
            render: (v: number | null | undefined) => `₩${Number(v ?? 0).toLocaleString()}`,
        },
        {
            title: "Total Expenses",
            render: (rec: any) => {
                const flight = Number(rec.flight?.total_flight_cost_krw ?? 0);
                const addl = Number(rec.trip?.additional_expenses ?? 0);
                return `₩${(flight + addl).toLocaleString()}`;
            },
        },
    ];

    if (!customer) return null;

    return (
        <div style={{padding: 24}}>
            <Button
                icon={<LeftOutlined/>}
                onClick={() => navigate(-1)}
                style={{marginBottom: 24}}
                className="rounded-md"
            >
                Back
            </Button>

            <Title level={3} style={{marginBottom: 16}}>
                <NoWrap>{customer.first_name} {customer.last_name} (MMID: {customer.mmid})</NoWrap>
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Casino Club Customer Overview
                            </Title>
                        </Col>
                    </Row>
                }
                extra={
                    <Button type="primary" onClick={showEditModal} className="rounded-md">
                        Edit
                    </Button>
                }
                loading={loading}
            >
                <Descriptions bordered column={1} style={maxW700}>
                    <Descriptions.Item label="First Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.first_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Last Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.last_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Email"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.registration.email}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="DOB"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.dob}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Nationality"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.nationality}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Address"
                                       labelStyle={boldLabelStyle}>{customer.address}</Descriptions.Item>
                    <Descriptions.Item label="Phone"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.phone}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Passport Number"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.passport_number}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Passport Expiration"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.passport_expiration}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="MMID"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.mmid}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Language"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.language || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="VIP Status"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.vip_status}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Person In-charge"
                                       labelStyle={boldLabelStyle}> <NoWrap>
                        {customer.person_in_charge
                            ? `${customer.person_in_charge.first_name} ${customer.person_in_charge.last_name}`
                            : "N/A"}
                    </NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Slot Player" labelStyle={boldLabelStyle}>
                        <NoWrap>{customer.slot_player ? "Yes" : "No"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Strong Slot Player" labelStyle={boldLabelStyle}>
                        <NoWrap>{customer.strong_slot_player ? "Yes" : "No"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Hotel Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.hotel_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Game Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.game_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Drink Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.drink_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Food Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.food_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Min Bet Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.min_bet_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Allergies"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.allergies || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Notes"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.notes || "N/A"}</NoWrap></Descriptions.Item>
                </Descriptions>

                <Divider style={{marginTop: 32}}/>
                <Title level={5} style={{marginBottom: 16}}>
                    Trip History
                </Title>

                <Table
                    rowKey={(r) => r.trip_id ?? r.id}
                    onRow={(record) => ({
                        onClick: () => navigate(`/cc/trips/${(record.trip_id ?? record.id)}/details/`),
                        style: {cursor: "pointer"},
                    })}
                    columns={tripColumns}
                    dataSource={trips}
                />

                <Modal
                    title={<span className="text-base font-semibold">Edit Customer</span>}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    okText="Save"
                    centered
                    width={700}
                    okButtonProps={{className: "rounded-md"}}
                    cancelButtonProps={{className: "rounded-md"}}
                >
                    <Divider className="my-2"/>

                    <Form
                        layout="vertical"
                        form={form}
                        onFinish={handleUpdate}
                        onValuesChange={(changedValues) => {
                            if ("slot_player" in changedValues && changedValues.slot_player === false) {
                                form.setFieldsValue({strong_slot_player: false});
                            }
                        }}
                        scrollToFirstError
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="first_name" label="First Name" rules={[{required: true}]}>
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="last_name" label="Last Name" rules={[{required: true}]}>
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="dob" label="DOB" rules={[{required: true}]}>
                                    <DatePicker style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="nationality" label="Nationality" rules={[{required: true}]}>
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="address" label="Address" rules={[{required: true}]}>
                                    <Input.TextArea rows={2}/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="phone" label="Phone" rules={[{required: true}]}>
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        {type: "email", message: "Please enter a valid email"},
                                    ]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="passport_expiration" label="Passport Expiration"
                                           rules={[{required: true}]}>
                                    <DatePicker style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="passport_number" label="Passport Number" rules={[{required: true}]}>
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="mmid" label="MMID">
                                    <Input disabled/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="language" label="Language">
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="vip_status" label="VIP Status">
                                    <Select>
                                        <Option value="Regular">Regular</Option>
                                        <Option value="VIP">VIP</Option>
                                        <Option value="VVIP">VVIP</Option>
                                        <Option value="Celebrity">Celebrity</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="person_in_charge_id" label="Person In Charge">
                                    <Select allowClear placeholder="Optional">
                                        {users.map(u => (
                                            <Option key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="slot_player"
                                    label="Slot Player"
                                    valuePropName="checked"
                                >
                                    <Switch/>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item shouldUpdate={(prev, curr) => prev.slot_player !== curr.slot_player}>
                                    {({getFieldValue}) => {
                                        const slotOn = !!getFieldValue("slot_player");

                                        return (
                                            <Form.Item
                                                name="strong_slot_player"
                                                label="Strong Slot Player"
                                                valuePropName="checked"
                                            >
                                                <Switch disabled={!slotOn}/>
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="hotel_preference" label="Hotel Preference">
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="game_preference" label="Game Preference">
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="drink_preference" label="Drink Preference">
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="food_preference" label="Food Preference">
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="min_bet_preference" label="Min Bet Preference">
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="allergies" label="Allergies">
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col span={24}>
                                <Form.Item name="notes" label="Notes">
                                    <Input.TextArea rows={3}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default CCCustomerDetailPage;